import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';
import './Login.css';

const ERROR_MESSAGES = {
  unauthorized_domain: 'Access restricted to @frontporchsolutions.com accounts only.',
  account_disabled: 'Your account has been disabled. Contact your administrator.',
  oauth_failed: 'Google sign-in failed. Please try again.',
  missing_code: 'Authentication was incomplete. Please try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  invalid_state: 'Security check failed. Please try signing in again.',
};

// Errors that suggest the user should try a different Google account
const ACCOUNT_SWITCH_ERRORS = ['unauthorized_domain'];

function Login() {
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();
  const error = searchParams.get('error');
  const showAccountSwitch = ACCOUNT_SWITCH_ERRORS.includes(error);

  const handleGoogleLogin = () => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    window.location.href = `${apiBase}/auth/google/login`;
  };

  const logoSrc = branding.login_logo || branding.sidebar_logo || '/logo-light.svg';
  const appName = branding.app_name || 'FPS OS';

  // Parse login prompt -- support line breaks
  const loginPromptText = branding.login_prompt || 'Sign in with your corporate account\n@frontporchsolutions.com';
  const promptParts = loginPromptText.split('\n');

  const loginFooterNote = branding.login_footer_note || 'Only @frontporchsolutions.com accounts can access';

  return (
    <div className="login-container">

      {/* Left Panel: Logo */}
      <div className="login-brand-panel">
        <img src={logoSrc} alt="Logo" className="login-logo-img" />
      </div>

      {/* Right Panel: Login */}
      <div className="login-right-panel">
        <div className="login-content">

          <h1 className="login-app-title">{appName}</h1>

          {error && (
            <div className="login-error">
              {ERROR_MESSAGES[error] || `Sign-in error: ${error}`}
              {showAccountSwitch && (
                <p className="login-error-hint">
                  Wrong Google account? Click below to choose a different one.
                </p>
              )}
            </div>
          )}

          <p className="login-prompt">
            {promptParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {part.startsWith('@') ? <strong>{part}</strong> : part}
              </React.Fragment>
            ))}
          </p>

          <hr className="login-divider" />

          <button className="google-signin-btn" onClick={handleGoogleLogin}>
            <span className="google-icon-pill">
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </span>
            {showAccountSwitch ? 'Try a different Google account' : 'Continue with Google'}
          </button>

          <p className="login-footer-note">
            {loginFooterNote}
          </p>
        </div>
      </div>

    </div>
  );
}

export default Login;
